import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  Radio,
  Row,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  DownloadOutlined,
  FileTextOutlined,
  ReloadOutlined,
  RobotOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import dayjs from 'dayjs';
import {
  generatePerformanceMarketReport,
  getPerformanceReportStatus,
  getSavedPerformanceReport,
  savePerformanceReportMarkdown,
} from '../services/api';
import { alexforbesBrand } from '../brand/alexforbes';
import BrandLogo from './BrandLogo';

const { Text } = Typography;
const { TextArea } = Input;

const PERIOD_OPTIONS = [
  { value: 'ytd', label: 'Year to date (YTD)' },
  { value: 'current', label: 'Month to date (current)' },
  { value: 'last_quarter', label: 'Last quarter' },
  { value: 'last_month', label: 'Last month' },
  { value: 'custom', label: 'Custom date range' },
];

function buildReportParams(period, customRange, benchmarkId) {
  const body = { period };
  if (benchmarkId) {
    body.benchmark = benchmarkId;
  }
  if (period === 'custom' && customRange?.length === 2) {
    body.start_date = customRange[0].format('YYYY-MM-DD');
    body.end_date = customRange[1].format('YYYY-MM-DD');
  }
  return body;
}

function applyReportResult(result, setters) {
  const {
    setReportMarkdown,
    setUnderlyingAttribution,
    setReportMeta,
    setHasSavedReport,
    setSavedAt,
    setFromCache,
    setPeriodStale,
  } = setters;
  setReportMarkdown(result.report_markdown || '');
  setUnderlyingAttribution(result.underlying_attribution || null);
  setReportMeta({
    period_label: result.period_label,
    start_date: result.start_date,
    end_date: result.end_date,
    expected_end_date: result.expected_end_date || null,
    summary: result.performance_summary,
  });
  setHasSavedReport(Boolean(result.saved || result.report_markdown));
  setSavedAt(result.saved_at || null);
  setFromCache(Boolean(result.from_cache));
  setPeriodStale(Boolean(result.period_stale));
}

const PortfolioPerformanceReport = ({ portfolioId, benchmarkId }) => {
  const [aiConfigured, setAiConfigured] = useState(null);
  const [period, setPeriod] = useState('ytd');
  const [customRange, setCustomRange] = useState(null);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [reportMeta, setReportMeta] = useState(null);
  const [underlyingAttribution, setUnderlyingAttribution] = useState(null);
  const [reportMarkdown, setReportMarkdown] = useState('');
  const [hasSavedReport, setHasSavedReport] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [fromCache, setFromCache] = useState(false);
  const [periodStale, setPeriodStale] = useState(false);
  const [activeTab, setActiveTab] = useState('preview');
  const [savingEdits, setSavingEdits] = useState(false);
  const previewRef = useRef(null);
  const skipNextSaveRef = useRef(false);
  const saveTimerRef = useRef(null);

  const setters = {
    setReportMarkdown,
    setUnderlyingAttribution,
    setReportMeta,
    setHasSavedReport,
    setSavedAt,
    setFromCache,
    setPeriodStale,
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await getPerformanceReportStatus(portfolioId);
        if (!cancelled) setAiConfigured(status.configured);
      } catch {
        if (!cancelled) setAiConfigured(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [portfolioId]);

  const loadSavedReport = useCallback(async () => {
    if (period === 'custom' && (!customRange || customRange.length !== 2)) {
      setReportMarkdown('');
      setUnderlyingAttribution(null);
      setReportMeta(null);
      setHasSavedReport(false);
      setSavedAt(null);
      setFromCache(false);
      setPeriodStale(false);
      return;
    }

    setLoadingSaved(true);
    setReportError(null);
    try {
      const params = buildReportParams(period, customRange, benchmarkId);
      const result = await getSavedPerformanceReport(portfolioId, params);
      if (result.saved && result.report_markdown) {
        skipNextSaveRef.current = true;
        applyReportResult(result, setters);
      } else {
        setReportMarkdown('');
        setUnderlyingAttribution(null);
        setReportMeta(null);
        setHasSavedReport(false);
        setSavedAt(null);
        setFromCache(false);
        setPeriodStale(false);
      }
    } catch {
      // No saved report — leave UI empty until user generates
    } finally {
      setLoadingSaved(false);
    }
  }, [portfolioId, period, customRange, benchmarkId]);

  useEffect(() => {
    loadSavedReport();
  }, [loadSavedReport]);

  const handleGenerate = useCallback(
    async (forceRegenerate = false) => {
      if (period === 'custom' && (!customRange || customRange.length !== 2)) {
        message.warning('Select a start and end date for the custom period');
        return;
      }

      setGenerating(true);
      setReportError(null);
      try {
        const body = {
          ...buildReportParams(period, customRange, benchmarkId),
          force_regenerate: forceRegenerate,
        };
        const result = await generatePerformanceMarketReport(portfolioId, body);
        if (result.error && !result.report_markdown) {
          setReportError(result.error);
          message.error(result.error);
          return;
        }
        skipNextSaveRef.current = true;
        applyReportResult(result, setters);
        setActiveTab('preview');
        if (result.from_cache) {
          message.info('Loaded saved report');
        } else {
          message.success('Market report generated and saved');
        }
      } catch (e) {
        const msg =
          e.response?.data?.error ||
          'Failed to generate report. Check OPENAI_API_KEY on the server.';
        setReportError(msg);
        message.error(msg);
      } finally {
        setGenerating(false);
      }
    },
    [portfolioId, period, customRange, benchmarkId]
  );

  useEffect(() => {
    if (!hasSavedReport || !reportMarkdown.trim()) return undefined;

    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return undefined;
    }

    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    saveTimerRef.current = setTimeout(async () => {
      setSavingEdits(true);
      try {
        await savePerformanceReportMarkdown(portfolioId, {
          ...buildReportParams(period, customRange, benchmarkId),
          report_markdown: reportMarkdown,
        });
      } catch {
        message.warning('Could not save report edits');
      } finally {
        setSavingEdits(false);
      }
    }, 1500);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [reportMarkdown, hasSavedReport, portfolioId, period, customRange, benchmarkId]);

  const handleExportPdf = useCallback(async () => {
    const el = previewRef.current;
    if (!el || !reportMarkdown.trim()) {
      message.warning('Generate a report first');
      return;
    }
    const confirmed = window.confirm(
      'This report is marked INTERNAL USE ONLY and is not for client distribution. Continue with PDF export?'
    );
    if (!confirmed) return;

    setActiveTab('preview');
    await new Promise((r) => setTimeout(r, 400));
    try {
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 12;
      const contentWidth = pageWidth - margin * 2;
      const imgHeight = (canvas.height * contentWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = margin;

      const stampInternal = () => {
        pdf.setFontSize(9);
        pdf.setTextColor(180, 60, 40);
        pdf.text(
          'INTERNAL USE ONLY — Not for client distribution',
          pageWidth / 2,
          8,
          { align: 'center' }
        );
        pdf.setTextColor(0, 0, 0);
      };

      stampInternal();
      pdf.addImage(imgData, 'PNG', margin, position, contentWidth, imgHeight);
      heightLeft -= pageHeight - margin * 2;

      while (heightLeft > 0) {
        pdf.addPage();
        stampInternal();
        position = margin - (imgHeight - heightLeft);
        pdf.addImage(imgData, 'PNG', margin, position, contentWidth, imgHeight);
        heightLeft -= pageHeight - margin * 2;
      }

      const filename = `INTERNAL-portfolio-report-${reportMeta?.start_date || 'report'}.pdf`;
      pdf.save(filename);
      message.success('Internal PDF downloaded');
    } catch (err) {
      console.error(err);
      message.error('Could not export PDF');
    }
  }, [reportMarkdown, reportMeta]);

  const handleClearReport = useCallback(() => {
    setReportMarkdown('');
    setUnderlyingAttribution(null);
    setReportMeta(null);
    setHasSavedReport(false);
    setSavedAt(null);
    setFromCache(false);
    setPeriodStale(false);
    setReportError(null);
  }, []);

  const disabledReason =
    aiConfigured === false
      ? 'Set OPENAI_API_KEY on the server to enable AI market reports'
      : null;

  const savedLabel = savedAt
    ? `Saved ${dayjs(savedAt).format('D MMM YYYY, HH:mm')}`
    : null;

  return (
    <Card
      size="small"
      title={
        <Space>
          <FileTextOutlined />
          <span>AI market report</span>
          {hasSavedReport && <Tag color="green">Saved</Tag>}
          {savingEdits && <Tag>Saving…</Tag>}
        </Space>
      }
      style={{ marginTop: 24 }}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Reports are saved per portfolio and period. When you return, the last saved report
        loads automatically. Regenerate only when you need a fresh AI draft.
      </Text>

      <Space direction="vertical" size="middle" style={{ width: '100%', marginBottom: 16 }}>
        <div>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Report period
          </Text>
          <Radio.Group
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            optionType="button"
            buttonStyle="solid"
            size="small"
          >
            {PERIOD_OPTIONS.map((opt) => (
              <Radio.Button key={opt.value} value={opt.value}>
                {opt.label}
              </Radio.Button>
            ))}
          </Radio.Group>
        </div>

        {period === 'custom' && (
          <DatePicker.RangePicker
            value={customRange}
            onChange={(vals) => setCustomRange(vals)}
            style={{ width: '100%', maxWidth: 360 }}
            disabledDate={(current) => current && current > dayjs().endOf('day')}
          />
        )}

        {benchmarkId && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Benchmark from chart comparison will be included in the report.
          </Text>
        )}

        <Space wrap>
          {!hasSavedReport ? (
            <Button
              type="primary"
              icon={<RobotOutlined />}
              loading={generating}
              onClick={() => handleGenerate(false)}
              disabled={!!disabledReason || loadingSaved}
              title={disabledReason || undefined}
              style={{
                background: alexforbesBrand.colors.teal,
                borderColor: alexforbesBrand.colors.teal,
              }}
            >
              Generate market report
            </Button>
          ) : (
            <>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                loading={generating}
                onClick={() => handleGenerate(true)}
                disabled={!!disabledReason || loadingSaved}
                title={disabledReason || undefined}
                style={{
                  background: alexforbesBrand.colors.teal,
                  borderColor: alexforbesBrand.colors.teal,
                }}
              >
                Regenerate report
              </Button>
              <Button onClick={loadSavedReport} loading={loadingSaved} disabled={generating}>
                Reload saved
              </Button>
            </>
          )}
        </Space>

        {aiConfigured === false && (
          <Alert
            type="info"
            showIcon
            message="OpenAI optional"
            description="Configure OPENAI_API_KEY in the server environment to generate AI market reports."
          />
        )}
      </Space>

      {loadingSaved && !reportMarkdown && (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Spin tip="Loading saved report…" />
        </div>
      )}

      {reportError && !reportMarkdown && (
        <Alert type="error" showIcon message={reportError} style={{ marginBottom: 16 }} />
      )}

      {periodStale && reportMeta && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Saved report period is out of date"
          description={
            <>
              This saved report ends on{' '}
              <Text strong>{dayjs(reportMeta.end_date).format('D MMM YYYY')}</Text>
              {reportMeta.expected_end_date && (
                <>
                  {' '}
                  but the current {period === 'ytd' ? 'YTD' : 'period'} runs through{' '}
                  <Text strong>
                    {dayjs(reportMeta.expected_end_date).format('D MMM YYYY')}
                  </Text>
                </>
              )}
              . Regenerate to refresh the report for the current period.
            </>
          }
          action={
            <Button
              size="small"
              type="primary"
              loading={generating}
              onClick={() => handleGenerate(true)}
              disabled={!!disabledReason}
            >
              Regenerate
            </Button>
          }
        />
      )}

      {reportMeta && (
        <Alert
          type={periodStale ? 'info' : 'success'}
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <Space>
              <span>{reportMeta.period_label}</span>
              {fromCache && <Tag color="blue">From saved</Tag>}
              {periodStale && <Tag color="orange">Stale period</Tag>}
            </Space>
          }
          description={
            <>
              {reportMeta.summary && (
                <span>
                  Return: {reportMeta.summary.change_percent}% ·{' '}
                  {reportMeta.summary.start_value} → {reportMeta.summary.end_value}{' '}
                  {reportMeta.summary.currency}
                </span>
              )}
              {savedLabel && (
                <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
                  {savedLabel}
                </Text>
              )}
            </>
          }
        />
      )}

      {reportMarkdown && (
        <>
          <Space style={{ marginBottom: 12 }}>
            <Button icon={<DownloadOutlined />} onClick={handleExportPdf}>
              Export PDF
            </Button>
            <Button type="link" onClick={handleClearReport}>
              Clear from view
            </Button>
          </Space>

          {underlyingAttribution?.holdings?.length > 0 && (
            <UnderlyingAttributionSummary attribution={underlyingAttribution} />
          )}

          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'preview',
                label: 'Preview',
                children: (
                  <div
                    ref={previewRef}
                    className="af-markdown-preview"
                    style={{
                      background: '#fff',
                      padding: 24,
                      border: '1px solid #e8e8e8',
                      borderRadius: 8,
                      maxWidth: 800,
                    }}
                  >
                    {reportMeta && (
                      <div
                        className="af-report-brand-header"
                        style={{ marginBottom: 16, paddingBottom: 12 }}
                      >
                        <BrandLogo variant="report" style={{ marginBottom: 8, maxWidth: 280 }} />
                        <Text type="secondary">{reportMeta.period_label}</Text>
                      </div>
                    )}
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{reportMarkdown}</ReactMarkdown>
                  </div>
                ),
              },
              {
                key: 'edit',
                label: 'Edit',
                children: (
                  <TextArea
                    value={reportMarkdown}
                    onChange={(e) => setReportMarkdown(e.target.value)}
                    autoSize={{ minRows: 14, maxRows: 28 }}
                    style={{ fontFamily: 'inherit', fontSize: 14 }}
                  />
                ),
              },
            ]}
          />
        </>
      )}

      {generating && (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin tip={hasSavedReport ? 'Regenerating report with OpenAI…' : 'Generating report with OpenAI…'} />
        </div>
      )}
    </Card>
  );
};

const performerColumns = [
  {
    title: 'Holding',
    dataIndex: 'name',
    key: 'name',
    ellipsis: true,
  },
  {
    title: 'Period return',
    dataIndex: 'period_return_percent',
    key: 'period_return_percent',
    width: 110,
    render: (v) => {
      const n = parseFloat(v);
      if (Number.isNaN(n)) return '—';
      const color = n >= 0 ? '#007A7A' : '#cf1322';
      const sign = n > 0 ? '+' : '';
      return (
        <Text style={{ color, fontVariantNumeric: 'tabular-nums' }}>
          {sign}
          {n.toFixed(2)}%
        </Text>
      );
    },
  },
  {
    title: 'Contribution',
    dataIndex: 'contribution_to_portfolio_percent',
    key: 'contribution',
    width: 110,
    render: (v) => `${v}%`,
  },
  {
    title: 'Proxy / context',
    key: 'proxy',
    ellipsis: true,
    render: (_, row) => (
      <Space size={4} wrap>
        {row.listed_proxy_ticker && <Tag>{row.listed_proxy_ticker}</Tag>}
        {row.asset_class && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.asset_class}
          </Text>
        )}
      </Space>
    ),
  },
];

function UnderlyingAttributionSummary({ attribution }) {
  const best = attribution.best_performers_by_return || [];
  const worst = attribution.worst_performers_by_return || [];
  const historyLimited = [...best, ...worst].some((r) => r.price_history_limited);

  return (
    <Card
      size="small"
      title="Underlying performance (period)"
      style={{ marginBottom: 16 }}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
        Holdings ranked by price return over the report period. The AI report expands on
        drivers using listed proxy tickers and asset-class context.
        {historyLimited
          ? ' Some returns use the first available NAV after the period start (price history does not cover the full window).'
          : ''}
      </Text>
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Text strong style={{ fontSize: 12 }}>
            Best performers
          </Text>
          <Table
            size="small"
            pagination={false}
            rowKey="isin"
            columns={performerColumns}
            dataSource={best}
            style={{ marginTop: 8, marginBottom: 16 }}
          />
        </Col>
        <Col xs={24} md={12}>
          <Text strong style={{ fontSize: 12 }}>
            Weakest performers
          </Text>
          <Table
            size="small"
            pagination={false}
            rowKey="isin"
            columns={performerColumns}
            dataSource={worst}
            style={{ marginTop: 8 }}
          />
        </Col>
      </Row>
    </Card>
  );
}

export default PortfolioPerformanceReport;
